import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

const GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

function computeCoords(triggerRect, panelWidth, panelHeight) {
  const anchorCenterX = triggerRect.left + triggerRect.width / 2;
  const maxLeft = window.innerWidth - panelWidth - VIEWPORT_MARGIN_PX;
  const left = Math.max(
    VIEWPORT_MARGIN_PX,
    Math.min(anchorCenterX - panelWidth / 2, maxLeft)
  );

  return {
    arrowLeft: Math.round(anchorCenterX - left),
    left: Math.round(left),
    top: Math.round(triggerRect.top - panelHeight - GAP_PX),
  };
}

/**
 * Fixed tooltip panel coords; closes on scroll or outside tap (mobile; no Popper).
 *
 * @param {React.RefObject<HTMLElement | null>} triggerRef
 * @param {React.RefObject<HTMLElement | null>} panelRef
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {{ closeOnScroll?: boolean }} [options]
 */
export function useMobileAnchoredTooltip(triggerRef, panelRef, isOpen, onClose, options = {}) {
  const {closeOnScroll = true} = options;
  const [coords, setCoords] = useState({arrowLeft: 0, isReady: false, left: 0, top: 0});
  const rafRef = useRef(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;

    if (!trigger || !panel) return;

    const {offsetWidth: w, offsetHeight: h} = panel;

    if (!w || !h) return;

    setCoords({...computeCoords(trigger.getBoundingClientRect(), w, h), isReady: true});
  }, [panelRef, triggerRef]);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updatePosition();
    });
  }, [updatePosition]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords({arrowLeft: 0, isReady: false, left: 0, top: 0});

      return undefined;
    }

    scheduleUpdate();
    const followupRaf = requestAnimationFrame(scheduleUpdate);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleUpdate) : null;

    if (panelRef.current) resizeObserver?.observe(panelRef.current);
    resizeObserver?.observe(document.body);

    const viewport = window.visualViewport;

    viewport?.addEventListener('resize', scheduleUpdate);

    return () => {
      cancelAnimationFrame(followupRaf);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      resizeObserver?.disconnect();
      viewport?.removeEventListener('resize', scheduleUpdate);
    };
  }, [isOpen, panelRef, scheduleUpdate]);

  useEffect(() => {
    if (!isOpen || !closeOnScroll) return undefined;

    const handleScroll = () => onClose();

    window.addEventListener('scroll', handleScroll, {capture: true, passive: true});
    window.visualViewport?.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, {capture: true});
      window.visualViewport?.removeEventListener('scroll', handleScroll);
    };
  }, [closeOnScroll, isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;

      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;

      onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown, {capture: true});

    return () => document.removeEventListener('pointerdown', handlePointerDown, {capture: true});
  }, [isOpen, onClose, panelRef, triggerRef]);

  return coords;
}
